import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ShieldCheck, Heart, Pill, FileText, Calendar, Users,
  QrCode, Plus, Activity, Utensils, Dumbbell, TrendingUp,
  Volume2, VolumeX, RefreshCw, Bell, Leaf, AlertTriangle, UserPlus
} from 'lucide-react';
import Layout from '../components/Layout';
import FamilySelector from '../components/FamilySelector';
import HealthTrends from '../components/HealthTrends';
import EncryptionBadge from '../components/EncryptionBadge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Health tips - rotating daily wellness advice
const healthTips = [
  { 
    tip: "Drink a glass of warm water with lime and a pinch of salt before breakfast - it kickstarts digestion!", 
    tipHi: "नाश्ते से पहले गर्म पानी में नींबू और चुटकी भर नमक पिएं - यह पाचन को तेज करता है!",
    icon: "💧", category: "digestion" 
  },
  { 
    tip: "Eat your last meal at least 2-3 hours before sleep for better rest and digestion.", 
    tipHi: "सोने से 2-3 घंटे पहले अंतिम भोजन करें - बेहतर नींद और पाचन के लिए।",
    icon: "🌙", category: "sleep" 
  },
  { 
    tip: "Add bitter foods like karela, methi, or neem to your diet - they help control blood sugar.", 
    tipHi: "करेला, मेथी या नीम जैसे कड़वे खाद्य पदार्थ खाएं - ये ब्लड शुगर नियंत्रित करते हैं।",
    icon: "🥬", category: "sugar" 
  },
  { 
    tip: "Take a 10-minute walk after meals - it helps lower blood sugar spikes by 30%.", 
    tipHi: "भोजन के बाद 10 मिनट टहलें - यह ब्लड शुगर को 30% तक कम करता है।",
    icon: "🚶", category: "sugar" 
  },
  { 
    tip: "Eat more greens! Spinach, palak, and methi are rich in iron and help reduce BP.", 
    tipHi: "हरी सब्जियां खाएं! पालक और मेथी में आयरन होता है और BP कम करने में मदद करता है।",
    icon: "🥗", category: "bp" 
  },
  { 
    tip: "Reduce salt intake - use herbs and spices like jeera, dhania for flavor instead.", 
    tipHi: "नमक कम करें - स्वाद के लिए जीरा, धनिया जैसे मसाले इस्तेमाल करें।",
    icon: "🧂", category: "bp" 
  },
  { 
    tip: "Deep breathing for 5 minutes daily can lower stress and blood pressure naturally.", 
    tipHi: "रोज 5 मिनट गहरी सांस लें - तनाव और BP स्वाभाविक रूप से कम होता है।",
    icon: "🧘", category: "bp" 
  },
  { 
    tip: "Soak almonds overnight and eat 5-6 in the morning for heart health.", 
    tipHi: "रात को बादाम भिगोएं और सुबह 5-6 खाएं - दिल की सेहत के लिए अच्छा है।",
    icon: "🥜", category: "heart" 
  },
  { 
    tip: "Chew your food 20-30 times - it improves digestion and helps you eat less.", 
    tipHi: "खाना 20-30 बार चबाएं - पाचन सुधरता है और कम खाना खाते हैं।",
    icon: "😋", category: "digestion" 
  },
  { 
    tip: "Replace white rice with brown rice or millets for better blood sugar control.", 
    tipHi: "सफेद चावल की जगह ब्राउन राइस या बाजरा खाएं - ब्लड शुगर नियंत्रण के लिए।",
    icon: "🍚", category: "sugar" 
  },
  { 
    tip: "Drink coconut water - it's natural, hydrating, and good for your kidneys.", 
    tipHi: "नारियल पानी पिएं - प्राकृतिक, हाइड्रेटिंग और किडनी के लिए अच्छा।",
    icon: "🥥", category: "hydration" 
  },
  { 
    tip: "Add turmeric (haldi) to your milk before bed - it reduces inflammation.", 
    tipHi: "सोने से पहले दूध में हल्दी मिलाएं - सूजन कम करती है।",
    icon: "🥛", category: "immunity" 
  },
  { 
    tip: "Eat dinner like a pauper - keep it light with soup, salad, or khichdi.", 
    tipHi: "रात का खाना हल्का रखें - सूप, सलाद या खिचड़ी खाएं।",
    icon: "🍲", category: "digestion" 
  },
  { 
    tip: "Stand up and stretch every hour if you sit for long - it improves circulation.", 
    tipHi: "लंबे समय तक बैठें तो हर घंटे उठकर स्ट्रेच करें - रक्त संचार सुधरता है।",
    icon: "🙆", category: "general" 
  },
  { 
    tip: "Include curd/yogurt daily - probiotics improve gut health and immunity.", 
    tipHi: "रोज दही खाएं - प्रोबायोटिक्स पेट और इम्युनिटी के लिए अच्छे हैं।",
    icon: "🥣", category: "digestion" 
  },
  { 
    tip: "Avoid screens 1 hour before bed for better sleep quality.", 
    tipHi: "सोने से 1 घंटे पहले मोबाइल/टीवी बंद करें - अच्छी नींद के लिए।",
    icon: "📱", category: "sleep" 
  },
  { 
    tip: "Eat a banana daily - potassium helps regulate blood pressure naturally.", 
    tipHi: "रोज एक केला खाएं - पोटैशियम BP नियंत्रित करता है।",
    icon: "🍌", category: "bp" 
  },
  { 
    tip: "Garlic on empty stomach helps lower cholesterol and boost immunity.", 
    tipHi: "खाली पेट लहसुन खाएं - कोलेस्ट्रॉल कम और इम्युनिटी बढ़ती है।",
    icon: "🧄", category: "heart" 
  },
  { 
    tip: "Replace evening chai with green tea - less caffeine, more antioxidants.", 
    tipHi: "शाम की चाय की जगह ग्रीन टी पिएं - कम कैफीन, ज्यादा एंटीऑक्सीडेंट।",
    icon: "🍵", category: "general" 
  },
  { 
    tip: "Laugh more! 15 minutes of laughter daily is like exercise for your heart.", 
    tipHi: "ज्यादा हंसें! रोज 15 मिनट हंसना दिल के लिए व्यायाम जैसा है।",
    icon: "😄", category: "heart" 
  },
];

// Get tip based on day and optionally user's health conditions
const getDailyTip = (userConditions = []) => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  
  // If user has specific conditions, prioritize relevant tips
  if (userConditions.length > 0) {
    const relevantTips = healthTips.filter(t => 
      userConditions.some(c => 
        (c.toLowerCase().includes('diabetes') || c.toLowerCase().includes('sugar')) && t.category === 'sugar' ||
        (c.toLowerCase().includes('bp') || c.toLowerCase().includes('hypertension') || c.toLowerCase().includes('blood pressure')) && t.category === 'bp' ||
        (c.toLowerCase().includes('heart') || c.toLowerCase().includes('cholesterol')) && t.category === 'heart'
      )
    );
    if (relevantTips.length > 0) {
      return relevantTips[dayOfYear % relevantTips.length];
    }
  }
  
  // Default: rotate through all tips based on day
  return healthTips[dayOfYear % healthTips.length];
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, speak, stopSpeaking, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [dailyTip, setDailyTip] = useState(healthTips[0]);
  const [userConditions, setUserConditions] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchUserConditions();
    // Check if a family member was selected from Family page
    const savedMember = localStorage.getItem('selectedFamilyMember');
    if (savedMember) {
      setSelectedMember(JSON.parse(savedMember));
    }
  }, []);

  // Handle speech toggle
  const toggleSpeech = (text) => {
    if (isSpeaking) {
      stopSpeaking();
      setIsSpeaking(false);
    } else {
      speak(text);
      setIsSpeaking(true);
      // Auto-reset after speech ends (approximate based on text length)
      const duration = Math.max(3000, text.length * 80);
      setTimeout(() => setIsSpeaking(false), duration);
    }
  };

  useEffect(() => {
    // Update tip when conditions are loaded
    setDailyTip(getDailyTip(userConditions));
  }, [userConditions]);

  const fetchUserConditions = async () => {
    try {
      const token = localStorage.getItem('sehatSetu_token');
      const response = await axios.get(`${API_URL}/health/medical-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const conditions = response.data?.map(h => h.condition) || [];
      setUserConditions(conditions);
    } catch (error) {
      console.log('Could not fetch conditions for tips');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sehat_setu_report_${user?.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const quickActions = [
    { icon: Heart, label: t('medicalHistory'), path: '/medical-history', color: 'bg-red-100 text-red-600' },
    { icon: Pill, label: t('prescriptions'), path: '/prescriptions', color: 'bg-blue-100 text-blue-600' },
    { icon: Leaf, label: language === 'hi' ? 'सप्लीमेंट्स' : 'Supplements', path: '/supplements', color: 'bg-green-100 text-green-600' },
    { icon: FileText, label: t('reports'), path: '/reports', color: 'bg-teal-100 text-teal-600' },
    { icon: Calendar, label: t('diary'), path: '/diary', color: 'bg-purple-100 text-purple-600' },
    { icon: Bell, label: t('reminders'), path: '/reminders', color: 'bg-amber-100 text-amber-600' },
    { icon: UserPlus, label: language === 'hi' ? 'परिवार' : 'Family', path: '/family', color: 'bg-pink-100 text-pink-600' },
    { icon: Users, label: t('referrals'), path: '/referrals', color: 'bg-orange-100 text-orange-600' },
    { icon: QrCode, label: t('doctorAccess'), path: '/doctor-access', color: 'bg-[#0D2340]/10 text-[#0D2340]' },
  ];

  const diaryQuickActions = [
    { icon: Activity, label: t('bloodPressure'), type: 'blood_pressure', color: 'bg-red-50 text-red-500' },
    { icon: Utensils, label: t('foodDiary'), type: 'food', color: 'bg-yellow-50 text-yellow-600' },
    { icon: Dumbbell, label: t('exercise'), type: 'exercise', color: 'bg-green-50 text-green-600' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#0D2340]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-nav w-full" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-[#0D2340] font-['Manrope']">
                {selectedMember && !selectedMember.is_self 
                  ? `${selectedMember.name}'s Health` 
                  : `${t('welcome')}, ${user?.name?.split(' ')[0]}!`}
              </h1>
              <FamilySelector 
                selectedMember={selectedMember} 
                onSelectMember={(member) => {
                  setSelectedMember(member);
                  localStorage.setItem('selectedFamilyMember', JSON.stringify(member));
                }}
              />
            </div>
            <p className="text-slate-600">
              {selectedMember && !selectedMember.is_self 
                ? `Managing health records for ${selectedMember.name}` 
                : t('healthOverview')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={downloadPDF}
              className="bg-[#FFB300] text-slate-900 hover:bg-[#FFB300]/90"
              data-testid="export-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              {t('exportPDF')}
            </Button>
          </div>
        </div>

        {/* Stats Overview - Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full" style={{ width: '100%' }}>
          {/* Vitals Summary - Full Width */}
          <Card className="sm:col-span-2 lg:col-span-3 bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] text-white" data-testid="vitals-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-['Manrope'] text-base">
                <Activity className="h-5 w-5" />
                {t('healthOverview')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {/* Horizontally scrollable stats with scroll indicator */}
              <div className="relative">
                <div className="overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  <div className="flex gap-4 min-w-max">
                    <StatBox label={t('medicalHistory')} value={stats?.medical_history_count || 0} icon={Heart} />
                    <StatBox label={t('prescriptions')} value={stats?.prescriptions_count || 0} icon={Pill} />
                    <StatBox label={t('reports')} value={stats?.reports_count || 0} icon={FileText} />
                    <StatBox label={t('diary')} value={stats?.diary_count || 0} icon={Calendar} />
                    <StatBox label={t('referrals')} value={stats?.referrals_count || 0} icon={Users} />
                    <StatBox label={t('activeAccess')} value={stats?.active_doctor_access || 0} icon={QrCode} />
                  </div>
                </div>
                {/* Scroll hint - gradient fade on right edge for mobile */}
                <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-[#0B7EA3] to-transparent pointer-events-none sm:hidden" />
              </div>
              
              {/* Recent Vitals Row - also scrollable */}
              {(stats?.recent_vitals?.blood_pressure || stats?.recent_vitals?.blood_sugar || stats?.recent_vitals?.weight) && (
                <div className="mt-3 relative">
                  <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    <div className="flex gap-3 min-w-max">
                    {stats?.recent_vitals?.blood_pressure && (
                      <div className="p-3 bg-white/10 rounded-lg min-w-[140px]">
                        <p className="text-xs opacity-70">{t('bloodPressure')}</p>
                        <p className="text-xl font-bold font-['Manrope']">
                          {stats.recent_vitals.blood_pressure.systolic}/{stats.recent_vitals.blood_pressure.diastolic}
                          <span className="text-sm font-normal ml-1">mmHg</span>
                        </p>
                        {stats.recent_vitals.blood_pressure.date && (
                          <p className="text-xs opacity-60 mt-1">
                            {new Date(stats.recent_vitals.blood_pressure.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                    {stats?.recent_vitals?.blood_sugar && (
                      <div className="p-3 bg-white/10 rounded-lg min-w-[140px]">
                        <p className="text-xs opacity-70">{language === 'hi' ? 'ब्लड शुगर' : 'Blood Sugar'}</p>
                        <p className="text-xl font-bold font-['Manrope']">
                          {stats.recent_vitals.blood_sugar.level}
                          <span className="text-sm font-normal ml-1">mg/dL</span>
                        </p>
                        {stats.recent_vitals.blood_sugar.date && (
                          <p className="text-xs opacity-60 mt-1">
                            {new Date(stats.recent_vitals.blood_sugar.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                    {stats?.recent_vitals?.weight && (
                      <div className="p-3 bg-white/10 rounded-lg min-w-[140px]">
                        <p className="text-xs opacity-70">{language === 'hi' ? 'वज़न' : 'Weight'}</p>
                        <p className="text-xl font-bold font-['Manrope']">
                          {stats.recent_vitals.weight.value}
                          <span className="text-sm font-normal ml-1">kg</span>
                        </p>
                        {stats.recent_vitals.weight.date && (
                          <p className="text-xs opacity-60 mt-1">
                            {new Date(stats.recent_vitals.weight.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Scroll hint for vitals */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0B7EA3] to-transparent pointer-events-none sm:hidden" />
              </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {quickActions.map((action, index) => (
            <Card
              key={index}
              className="card-interactive cursor-pointer"
              onClick={() => navigate(action.path)}
              data-testid={`quick-action-${action.path.replace('/', '')}`}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className={`p-4 rounded-xl ${action.color}`}>
                  <action.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-semibold text-lg font-['Manrope']">{action.label}</p>
                  <p className="text-sm text-slate-500">{t('view')}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Health Trends Section */}
        <div>
          <h2 className="text-lg font-semibold text-[#0D2340] mb-3 font-['Manrope']">
            {language === 'hi' ? '📊 आपकी सेहत के रुझान' : '📊 Your Health Trends'}
          </h2>
          <HealthTrends familyMemberId={selectedMember?.is_self ? null : selectedMember?.id} />
        </div>

        {/* Zero Liability Encryption Badge */}
        <EncryptionBadge className="my-2" />

        {/* Diary Quick Actions */}
        <Card data-testid="diary-quick-actions">
          <CardHeader>
            <CardTitle className="font-['Manrope']">{t('diary')} - {t('quickActions')}</CardTitle>
            <CardDescription>{t('addEntry')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {diaryQuickActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className={`h-20 flex-col gap-2 ${action.color} border-transparent hover:border-[#FFB300]`}
                  onClick={() => navigate(`/diary?type=${action.type}`)}
                  data-testid={`diary-action-${action.type}`}
                >
                  <action.icon className="h-6 w-6" />
                  <span className="text-xs">{action.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Health Tip - Dynamic */}
        <Card className="border-[#FFB300] bg-gradient-to-r from-[#FFB300]/10 to-[#FFB300]/5" data-testid="health-tip">
          <CardContent className="p-4 flex items-start gap-4">
            <div className="text-3xl flex-shrink-0">
              {dailyTip.icon}
            </div>
            <div className="flex-1">
              <p className="font-semibold font-['Manrope'] text-[#0D2340] mb-1">
                {language === 'hi' ? 'आज की सेहत टिप' : "Today's Health Tip"}
              </p>
              <p className="text-slate-700 leading-relaxed">
                {language === 'hi' && dailyTip.tipHi ? dailyTip.tipHi : dailyTip.tip}
              </p>
            </div>
            <Button
              variant={isSpeaking ? "destructive" : "ghost"}
              size="icon"
              onClick={() => toggleSpeech(language === 'hi' && dailyTip.tipHi ? dailyTip.tipHi : dailyTip.tip)}
              className={`flex-shrink-0 ${isSpeaking ? 'animate-pulse' : ''}`}
              data-testid="speak-tip-btn"
            >
              {isSpeaking ? (
                <VolumeX className="h-5 w-5" />
              ) : (
                <Volume2 className="h-5 w-5 text-slate-600" />
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Family Health Vault Banner - Bottom */}
        <Card 
          className="border-2 border-pink-300 bg-gradient-to-r from-pink-50 to-purple-50 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/family')}
          data-testid="family-vault-banner"
        >
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl">
                <UserPlus className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold text-purple-700 font-['Manrope']">
                  {language === 'hi' ? 'परिवार स्वास्थ्य वॉल्ट' : 'Family Health Vault'}
                </h3>
                <p className="text-sm text-purple-600">
                  {language === 'hi' 
                    ? 'दादा-दादी, माता-पिता, बच्चों की सेहत संभालें' 
                    : 'Manage health records for parents, grandparents & kids'}
                </p>
              </div>
              <Users className="h-10 w-10 text-purple-500 hidden md:block" />
            </div>
          </CardContent>
        </Card>

        {/* Emergency QR Card Banner - Bottom */}
        <Card 
          className="border-2 border-red-300 bg-gradient-to-r from-red-50 to-red-100 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/emergency-profile')}
          data-testid="emergency-card-banner"
        >
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-red-600 rounded-xl">
                <AlertTriangle className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold text-red-700 font-['Manrope']">
                  {language === 'hi' ? 'आपातकालीन QR कार्ड' : 'Emergency QR Card'}
                </h3>
                <p className="text-sm text-red-600">
                  {language === 'hi' 
                    ? 'अपनी आपातकालीन जानकारी QR कोड से शेयर करें - मुफ्त!' 
                    : 'Share critical health info via QR code - FREE!'}
                </p>
              </div>
              <QrCode className="h-10 w-10 text-red-600 hidden md:block" />
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const StatBox = ({ label, value, icon: Icon }) => (
  <div className="text-center">
    <Icon className="h-5 w-5 mx-auto mb-1 opacity-70" />
    <p className="text-2xl font-bold font-['Manrope']">{value}</p>
    <p className="text-xs opacity-80">{label}</p>
  </div>
);

export default Dashboard;
